/**
 * TwoBoneIk — Analytic two-bone IK solver for arms and legs.
 *
 * Given root, mid, tip bones + target position + pole target (hint direction),
 * computes joint rotations analytically using the law of cosines.
 *
 * Handles:
 *   - Target out of reach (clamps to max extension)
 *   - Zero-length bones (degenerate case)
 *   - Degenerate pole target (falls back to automatic perpendicular)
 *   - Weight blending (lerps between original and solved poses)
 */

import * as THREE from 'three/webgpu';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface TwoBoneIkInput {
  /** Root bone (hip/shoulder). */
  root: THREE.Bone;
  /** Mid bone (knee/elbow). */
  mid: THREE.Bone;
  /** Tip bone (ankle/wrist). */
  tip: THREE.Bone;
  /** Target world-space position. */
  targetPos: THREE.Vector3;
  /** Optional target world-space rotation for the tip bone. */
  targetRot?: THREE.Quaternion;
  /** Optional pole target world-space position (hint for bend direction). */
  poleTarget?: THREE.Vector3;
  /** Blend weight 0-1 (0 = original pose, 1 = fully solved). */
  weight?: number;
}

// ─── Scratch objects (avoid per-frame allocation) ────────────────────────────

const _rootPos = new THREE.Vector3();
const _midPos = new THREE.Vector3();
const _tipPos = new THREE.Vector3();
const _rootToTarget = new THREE.Vector3();
const _rootToMid = new THREE.Vector3();
const _midToTip = new THREE.Vector3();
const _poleDir = new THREE.Vector3();
const _bendAxis = new THREE.Vector3();
const _perp = new THREE.Vector3();
const _q0 = new THREE.Quaternion();
const _q1 = new THREE.Quaternion();
const _parentInvQ = new THREE.Quaternion();

// ─── Helper: safe quaternion difference ──────────────────────────────────────

/**
 * Compute quaternion needed to rotate `from` direction to `to` direction.
 * Returns identity if vectors are parallel/anti-parallel within epsilon.
 */
function quatFromTo(from: THREE.Vector3, to: THREE.Vector3): THREE.Quaternion {
  const dot = Math.max(-1, Math.min(1, from.dot(to)));
  if (dot > 0.9999) return new THREE.Quaternion(); // identity
  if (dot < -0.9999) {
    // Anti-parallel: find an arbitrary perpendicular axis
    _perp.set(1, 0, 0);
    if (Math.abs(from.x) > 0.9) _perp.set(0, 1, 0);
    _perp.crossVectors(from, _perp).normalize();
    return new THREE.Quaternion().setFromAxisAngle(_perp, Math.PI);
  }
  return new THREE.Quaternion().setFromUnitVectors(from, to);
}

/**
 * Convert a world-space quaternion to bone local space.
 */
function worldToLocal(bone: THREE.Bone, worldQuat: THREE.Quaternion): THREE.Quaternion {
  if (!bone.parent) return worldQuat.clone();
  bone.parent.updateWorldMatrix(true, false);
  bone.parent.getWorldQuaternion(_parentInvQ);
  _parentInvQ.invert();
  return _parentInvQ.clone().multiply(worldQuat);
}

// ─── TwoBoneIkSolver ─────────────────────────────────────────────────────────

/**
 * Solve a two-bone IK chain analytically.
 *
 * Algorithm:
 * 1. Measure bone lengths L1 (root→mid) and L2 (mid→tip).
 * 2. Compute distance D from root to target; clamp to L1+L2.
 * 3. Use law of cosines to find angles at root and mid joints.
 * 4. Use the pole target to establish the bend plane normal.
 * 5. Rotate root so the chain points toward target in the bend plane.
 * 6. Rotate mid by the required knee/elbow angle.
 * 7. Optionally rotate tip to match target rotation.
 * 8. Blend with original pose using weight.
 */
export function solveTwoBone(input: TwoBoneIkInput): void {
  const { root, mid, tip, targetPos, targetRot, poleTarget, weight = 1.0 } = input;

  if (weight <= 0) return;

  // ── Step 1: Update matrices and get world positions ──────────────────────
  root.updateWorldMatrix(true, false);
  mid.updateWorldMatrix(true, false);
  tip.updateWorldMatrix(true, false);

  _rootPos.setFromMatrixPosition(root.matrixWorld);
  _midPos.setFromMatrixPosition(mid.matrixWorld);
  _tipPos.setFromMatrixPosition(tip.matrixWorld);

  // ── Step 2: Compute bone segment lengths ─────────────────────────────────
  const L1 = _rootPos.distanceTo(_midPos);
  const L2 = _midPos.distanceTo(_tipPos);

  if (L1 < 1e-6 || L2 < 1e-6) return; // Degenerate chain

  // ── Step 3: Clamp target to reachable range ───────────────────────────────
  _rootToTarget.subVectors(targetPos, _rootPos);
  const D = Math.min(_rootToTarget.length(), L1 + L2 - 1e-4);
  if (D < 1e-6) return; // target on the root joint: cosRoot = 0/0 → NaN bone quaternion
  // Build the clamped target along the original direction
  const targetDir = _rootToTarget.clone().normalize();
  const clampedTarget = _rootPos.clone().addScaledVector(targetDir, D);

  // ── Step 4: Establish bend plane using pole target ────────────────────────
  // The bend plane contains root → target. The pole target biases which side
  // the mid joint bends toward.

  if (poleTarget) {
    _poleDir.subVectors(poleTarget, _rootPos);
    // Project out the component along root→target to get perpendicular
    const along = _poleDir.dot(targetDir);
    _poleDir.addScaledVector(targetDir, -along);
  } else {
    // Default: use the mid-joint's current offset from the root-target line
    _poleDir.subVectors(_midPos, _rootPos);
    const along = _poleDir.dot(targetDir);
    _poleDir.addScaledVector(targetDir, -along);
  }

  if (_poleDir.lengthSq() < 1e-10) {
    // Degenerate: pole is on the line; pick arbitrary perpendicular
    _poleDir.set(0, 0, 1);
    if (Math.abs(targetDir.dot(_poleDir)) > 0.9) _poleDir.set(0, 1, 0);
    _poleDir.crossVectors(targetDir, _poleDir).normalize();
  } else {
    _poleDir.normalize();
  }

  // Bend axis perpendicular to both pole and target directions
  _bendAxis.crossVectors(_poleDir, targetDir).normalize();
  if (_bendAxis.lengthSq() < 1e-10) {
    _bendAxis.set(1, 0, 0); // fallback
  }

  // ── Step 5: Law of cosines to find joint angles ───────────────────────────
  //
  //   cos(angleAtRoot) = (L1² + D² - L2²) / (2 * L1 * D)
  //   cos(angleAtMid)  = (L1² + L2² - D²) / (2 * L1 * L2)
  //
  const L1sq = L1 * L1;
  const L2sq = L2 * L2;
  const Dsq = D * D;

  const cosRoot = Math.max(-1, Math.min(1, (L1sq + Dsq - L2sq) / (2 * L1 * D)));
  // (Mid-bone angle is solved geometrically below via `quatFromTo` between
  // `rotatedMidToTip` and `midToTipDesired`, so the analytic
  // law-of-cosines mid angle is not needed. Removed to prevent a refactor
  // trap where a future contributor wires `setFromAxisAngle(_bendAxis,
  // angleAtMid)` and gets a different — and wrong, given the existing
  // root solve — output.)

  const angleAtRoot = Math.acos(cosRoot);

  // ── Step 6: Solve root bone rotation ─────────────────────────────────────
  // Original root-to-mid direction
  root.updateWorldMatrix(true, false);
  mid.updateWorldMatrix(true, false);
  _rootToMid.subVectors(_midPos, _rootPos).normalize();

  // Desired root-to-mid: rotate targetDir by angleAtRoot around bendAxis
  // toward the pole side
  const rootToMidDesired = targetDir.clone();
  _q0.setFromAxisAngle(_bendAxis, -angleAtRoot);
  rootToMidDesired.applyQuaternion(_q0).normalize();

  // Rotate root so _rootToMid aligns with rootToMidDesired
  const rootRotDelta = quatFromTo(_rootToMid, rootToMidDesired);

  // Apply to root in world space
  root.getWorldQuaternion(_q1);
  const newRootWorldQuat = rootRotDelta.clone().multiply(_q1);

  // ── Step 7: Solve mid bone rotation ──────────────────────────────────────
  mid.updateWorldMatrix(true, false);
  tip.updateWorldMatrix(true, false);
  _midToTip.subVectors(_tipPos, _midPos).normalize();

  // After root rotates, mid will be at rootPos + L1 * rootToMidDesired
  const newMidPos = _rootPos.clone().addScaledVector(rootToMidDesired, L1);

  // Desired mid-to-tip: from newMidPos toward clampedTarget, length L2
  const midToTipDesired = clampedTarget.clone().sub(newMidPos).normalize();

  // Use the original mid-to-tip direction rotated by rootRotDelta
  const rotatedMidToTip = _midToTip.clone().applyQuaternion(rootRotDelta);

  const midRotDelta = quatFromTo(rotatedMidToTip, midToTipDesired);

  mid.getWorldQuaternion(_q0);
  // Apply root rotation to mid first, then mid rotation
  const newMidWorldQuat = midRotDelta.clone().multiply(rootRotDelta.clone().multiply(_q0));

  // ── Step 8: Apply with weight blending ────────────────────────────────────
  // Save original local quats for blending
  const origRootLocal = root.quaternion.clone();
  const origMidLocal = mid.quaternion.clone();
  const origTipLocal = tip.quaternion.clone();

  // Convert world quats to local space — the ROOT first, applied, and only then the mid against the
  // root's NEW world rotation. WHY (2026-09-25, foot IK): the mid's local was taken against the root's
  // OLD world quaternion while its world target already contained the root's rotation, so the root delta
  // was applied twice and the tip missed a reachable target by ~0.3 m on a 0.9 m leg
  // (tests/unit/two-bone-ik-reach.test.ts).
  const newRootLocal = worldToLocal(root, newRootWorldQuat);
  root.quaternion.copy(origRootLocal).slerp(newRootLocal, weight);
  root.updateMatrix();
  root.updateWorldMatrix(false, false);
  const newMidLocal = worldToLocal(mid, newMidWorldQuat);
  mid.quaternion.copy(origMidLocal).slerp(newMidLocal, weight);
  mid.updateMatrix();

  // ── Step 9: Apply tip rotation if requested ───────────────────────────────
  if (targetRot) {
    const origTipLocal2 = origTipLocal;
    const newTipLocal = worldToLocal(tip, targetRot);
    tip.quaternion.copy(origTipLocal2).slerp(newTipLocal, weight);
    tip.updateMatrix();
  }

  // Force matrix updates to propagate
  root.updateWorldMatrix(false, true);
}
